import { useQuery, useMutation } from '@tanstack/react-query';
import request from 'shared/lib/request';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { showMessage } from 'shared/feedback/messages';
import { i18n, type MessageKey } from 'shared/i18n';
import { permissionGroupKeys } from './keys';
import { api } from 'shared/api/client';

// These hooks resolve a username against the /registry contract (users service)
// before hitting /gaby, so they mix the typed `api` client with the raw request
// wrapper: /registry is not in the document OpenAPI contract and cannot be typed
// until that service publishes one. The /gaby-only siblings live in ./queries.ts.

class UserNotFound extends Error {}

const organizationUsers = async (organization: unknown) => {
  const res = await request.get(`/registry/users?attributeId=${organization}&size=1000`);
  return (res.data?._embedded?.users ?? []) as { id: number; userName: string }[];
};

const findOrganizationUser = async (organization: unknown, userName: string) => {
  const user = (await organizationUsers(organization)).find((u) => u.userName === userName);
  if (!user) throw new UserNotFound(userName);
  return user;
};

const userNotFoundOr = (message: MessageKey) => (e: unknown) => e instanceof UserNotFound ? showMessage('warning', i18n(message)) : ServerErrorHandler(e);

export const useAddDocumentPermissionUser = () =>
  useMutation({
    mutationFn: async ({
      documentId,
      permission,
      userName,
      organization,
    }: {
      documentId: number | string;
      permission: string;
      userName: string;
      organization: unknown;
    }) => {
      const user = await findOrganizationUser(organization, userName);
      await api.POST('/documents/{superId}/permissions/{permission}/users', {
        params: { path: { superId: Number(documentId), permission } },
        body: { userId: user.id },
      });
      return { user, permission };
    },

    onError: userNotFoundOr('document-permissions:add-user.not-found'),
  });

export const useSectionPermissions = (documentId: number | string, selectedOrganization: unknown) =>
  useQuery({
    queryKey: permissionGroupKeys.sectionPermissions(documentId, selectedOrganization),

    queryFn: async () => {
      const [permissionsRes, usersRes, documentRes] = await Promise.all([
        api.GET('/documents/{superId}/permissions', { params: { path: { superId: Number(documentId) } } }),
        organizationUsers(selectedOrganization),
        api.GET('/documents/', {
          params: { query: { superId: Number(documentId), organization: String(selectedOrganization), current: 'true' } },
        }),
      ]);
      return {
        permissions: permissionsRes.data,
        users: usersRes,
        document: documentRes.data[0],
      };
    },

    enabled: !!(documentId && selectedOrganization),
  });

export const useAddUserToPermissionGroup = () =>
  useMutation({
    mutationFn: async ({ permissionGroupId, userName, organization }: { permissionGroupId: number; userName: string; organization: unknown }) => {
      const user = await findOrganizationUser(organization, userName);
      await api.POST('/permission-groups/{permissionGroup}/users', {
        params: { path: { permissionGroup: permissionGroupId } },
        body: { userId: user.id },
      });
    },

    onError: userNotFoundOr('section-permissions:user-not-found'),
  });
